'use client';

import { Children, createContext, ReactNode, useContext } from 'react';
import { usePathname } from 'next/navigation';
import { cn, composeTailwindRenderProps } from '@utils';
import {
	Breadcrumb,
	Breadcrumbs as BreadcrumbsPrimitive,
	Link as LinkPrimitive,
	LinkProps as LinkPrimitiveProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

import type { BreadcrumbProps, BreadcrumbsProps } from 'react-aria-components';

import { ChevronLeft, ChevronRight } from '../icons';

type BreadcrumbsContextProps = {
	theme?: 'light' | 'dark';
	isSingleItem?: boolean;
};

const BreadcrumbsContext = createContext<BreadcrumbsContextProps>({
	theme: 'light',
	isSingleItem: false,
});

const Breadcrumbs = <T extends object>({
	className,
	children,
	...props
}: BreadcrumbsProps<T> & { theme?: 'light' | 'dark' } & { children?: ReactNode }) => {
	const childrenArray = Children.toArray(children);
	const isSingleItem = childrenArray.length === 1;

	return (
		<BreadcrumbsContext.Provider value={{ theme: props.theme, isSingleItem }}>
			<BreadcrumbsPrimitive {...props} className={cn('flex items-center gap-1', className)}>
				{children}
			</BreadcrumbsPrimitive>
		</BreadcrumbsContext.Provider>
	);
};

interface BreadcrumbsItemProps extends BreadcrumbProps {
	href?: string;
	theme?: 'light' | 'dark';
}

const BreadcrumbsItem = ({
	href,
	className,
	...props
}: BreadcrumbsItemProps & Partial<Omit<LinkProps, 'className'>>) => {
	const { theme: contextTheme, isSingleItem } = useContext(BreadcrumbsContext);
	const theme = props.theme ?? contextTheme;

	const pathname = usePathname();
	const isCurrent = href === pathname;

	return (
		<Breadcrumb
			{...props}
			className={composeTailwindRenderProps(className, 'flex items-center gap-1')}
		>
			{isSingleItem && !isCurrent && (
				<ChevronLeft className="size-icon-xs text-light-action-secondary" />
			)}
			<Link {...props} href={href} theme={theme} isCurrent={isCurrent} isDisabled={isCurrent} />
			{!isSingleItem && !isCurrent && (
				<ChevronRight className="size-icon-xs text-light-action-secondary" />
			)}
		</Breadcrumb>
	);
};

interface LinkProps extends LinkPrimitiveProps {
	theme?: 'light' | 'dark';
	isCurrent?: boolean;
}

const Link = ({ className, theme, isCurrent, ...props }: LinkProps) => {
	const linkStyle = tv({
		base: [
			'w-fit rounded-xs text-xs whitespace-nowrap underline decoration-1 underline-offset-2 focus-visible:underline lg:text-sm',
		],
		variants: {
			theme: {
				light:
					'text-light-link visited:text-light-link decoration-light-link visited:decoration-light-link',
				dark: 'text-neutral-white decoration-neutral-white visited:text-neutral-white visited:decoration-neutral-white',
			},
			isCurrent: {
				true: 'text-light-text-lighter decoration-transparent',
			},
		},
		defaultVariants: {
			theme: 'light',
		},
	});

	return <LinkPrimitive className={cn(linkStyle({ theme, isCurrent }), className)} {...props} />;
};

Breadcrumbs.Item = BreadcrumbsItem;

export type { BreadcrumbsProps, BreadcrumbsItemProps };
export { Breadcrumbs };
